import MEMBER_ROLES from '@data/constants/MemberRoles'
import entityIdSchema from '@data/schemas/entityId'
import { tail } from 'lodash-es'

const MEMBERSHIP_ROLES: TMemberRole[] = [
    MEMBER_ROLES.ADMIN,
    MEMBER_ROLES.AMBASSADOR,
    MEMBER_ROLES.ACTIVITY_GROUP_CONSUL,
    MEMBER_ROLES.NEWCOMER_BUDDY,
    MEMBER_ROLES.ALBATROSS,
    MEMBER_ROLES.USER,
]

export const getMembership = (userRoles: TUserRole[], roles: TMemberRole[] = MEMBERSHIP_ROLES): TMemberRole => {
    if (roles.length === 0 || !userRoles || userRoles.length === 0) {
        return 'ROLE_USER'
    }

    const [role] = roles

    return role && userRoles.includes(role) ? role : getMembership(userRoles, tail(roles))
}

const isActiveRole = (userRoles: TUserRole[], role: TUserRole): boolean => getMembership(userRoles) === role

const hasRole = (userRoles: TUserRole[], role: TUserRole): boolean =>
    userRoles && userRoles.length > 0 && userRoles.includes(role)

export const isBasicUser = (userRoles: TUserRole[]): boolean => isActiveRole(userRoles, MEMBER_ROLES.USER)

export const isAdminUser = (userRoles: TUserRole[]): boolean => hasRole(userRoles, MEMBER_ROLES.ADMIN)

export const isAmbassador = (userRoles: TUserRole[]): boolean => hasRole(userRoles, MEMBER_ROLES.AMBASSADOR)

export const isConsul = (userRoles: TUserRole[]): boolean => hasRole(userRoles, MEMBER_ROLES.ACTIVITY_GROUP_CONSUL)

export const isVolunteer = (userRoles: TUserRole[]): boolean => isAmbassador(userRoles) || isConsul(userRoles)

export const isConsulOfGroup = (userId: TCurrentUser['id'] | TMember['id'], group: TGroup): boolean =>
    group.consulIds.includes(userId)

export const isNewcomerBuddy = (userRoles: TUserRole[]): boolean => hasRole(userRoles, MEMBER_ROLES.NEWCOMER_BUDDY)

export const isAlbatross = (userRoles: TUserRole[]): boolean => hasRole(userRoles, MEMBER_ROLES.ALBATROSS)

export const getHostType = (
    userId: TCurrentUser['id'] | TMember['id'],
    calendarEntry: TCalendarEntry
): 'ambassador' | 'consul' | null => {
    const hostIds = calendarEntry.hosts?.map((host) => host.id)

    if (hostIds?.includes(userId)) {
        return calendarEntry.type === 'event' ? 'ambassador' : 'consul'
    }

    return null
}

export const isConsulOnly = (userRoles: TUserRole[]): boolean => isConsul(userRoles) && !isAmbassador(userRoles)

export function toMemberId(memberId: unknown): TMember['id'] {
    if (typeof memberId === 'string') {
        memberId = Number(memberId)
    }

    return entityIdSchema<'Member'>().validateSync(memberId)
}

export function isMemberId(memberId: unknown): memberId is TMember['id'] {
    return entityIdSchema<'Member'>().isValidSync(memberId)
}
